import React, { useEffect, useState } from 'react';
import {
  TextInput,
  Switch,
  Button,
  Box,
  Accordion,
  Text,
  // Select, // Temporarily hidden — categories UI disabled
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/Store';
import { ITable } from '@/types/Table';
import type { LinkShareMode } from '@/types/Product.model';
import { tagsStringToArray } from '@/utils/tags';
// Temporarily hidden — categories UI disabled
// import { PRODUCT_CATEGORIES } from '@/constants/productCategories';
import { LinkAccessControl } from '@/components/ProductShare/LinkAccessControl';
import { MetadataTableEditor } from '../MetadataTableEditor/MetadataTableEditor';
import { TagsField } from '../TagsField/TagsField';
import { ProductRichTextEditor } from '../ProductRichTextEditor/ProductRichTextEditor';

export interface ProductFormData {
  productName: string;
  productPage: string;
  articleNumber: string;
  isPublic: boolean;
  descriptionHtml: string;
  linkShareMode: LinkShareMode;
  linkSharePassword: string;
  updateLinkSharePassword: boolean;
  /** Optional fixed category slug */
  category?: string;
  metadata?: ITable;
}

interface ProductFormProps {
  onSubmit: (data: ProductFormData) => void;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  isEditing?: boolean;
  initialValues?: Partial<ProductFormData>;
  /** Platform-suggested tags for the Tags field */
  suggestedTags?: string[];
  /** Tags the current user has used on their own products before */
  previousTags?: string[];
  /** When set, assigns this id to the form element (for submit buttons outside via the form attribute). */
  formId?: string;
  /** Hide the default submit row (e.g. parent renders submit next to preview). */
  hideSubmitButton?: boolean;
  /** Edit mode: server indicates a link-share password already exists (never the plaintext). */
  initialLinkSharePasswordSet?: boolean;
  /** Live form values for dirty-state tracking in parent pages. */
  onValuesChange?: (values: ProductFormData) => void;
  /** When set after a model upload, applies a filename-derived product name. */
  modelUploadProductName?: string;
}

export function ProductForm({
  onSubmit,
  isSubmitting = false,
  isDisabled = false,
  isEditing = false,
  initialValues,
  suggestedTags = [],
  previousTags = [],
  formId,
  hideSubmitButton = false,
  initialLinkSharePasswordSet = false,
  onValuesChange,
  modelUploadProductName,
}: ProductFormProps) {
  const { t } = useTranslation();
  const color: string = useStore((state) => state.userProfile.qrCodeColor);

  const form = useForm<ProductFormData>({
    initialValues: {
      productName: '',
      productPage: '',
      articleNumber: '',
      isPublic: true,
      descriptionHtml: '',
      linkShareMode: 'open',
      linkSharePassword: '',
      updateLinkSharePassword: false,
      category: '',
      metadata: {
        _id: '',
        name: '',
        columns: {},
        rows: {
          default: {
            pNetId: 'default',
            data: {},
          },
        },
      },
      ...initialValues,
    },
    validate: {
      productPage: (value) =>
        !value ||
        /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([\/\w.%+-]*)*\/?([?#][\w\-._~:\/?#[\]@!$&'()*+,;=%]*)?$/.test(
          value
        )
          ? null
          : t('editProduct.invalidUrl'),
      productName: (value) =>
        /^(?=.*[a-zA-Z\u00E4\u00F6\u00FC\u00DF\u00C4\u00D6\u00DC])[a-zA-Z\u00E4\u00F6\u00FC\u00DF\u00C4\u00D6\u00DC0-9_\s\.-]{1,50}$/.test(value) ? null : t('editProduct.invalidProductName'),
      articleNumber: (value) => {
        if (!value) return null; // Empty value is valid

        // Check if it's a valid UTF-8 string that can be JSON serialized
        try {
          // Test JSON serialization
          JSON.stringify(value);

          // Check for reasonable length (max 200 characters)
          if (value.length > 200) {
            return t('editProduct.articleNumberTooLong');
          }

          return null;
        } catch (error) {
          return t('editProduct.invalidArticleNumber');
        }
      },
      linkSharePassword: (value, values) => {
        if (values.linkShareMode !== 'password') return null;
        const pwd = (value ?? '').trim();
        if (!isEditing) {
          return pwd.length >= 4 ? null : t('editProduct.linkShare.passwordTooShort');
        }
        if (initialLinkSharePasswordSet && !values.updateLinkSharePassword) return null;
        return pwd.length >= 4 ? null : t('editProduct.linkShare.passwordTooShort');
      },
    },
  });

  // Sync server snapshot for core fields; keep the user's link-share segment until save reloads it.
  useEffect(() => {
    if (!initialValues || !isEditing) return;
    form.setValues((current) => ({
      ...current,
      productName: initialValues.productName || '',
      productPage: initialValues.productPage || '',
      articleNumber: initialValues.articleNumber || '',
      isPublic: initialValues.isPublic ?? false,
      descriptionHtml: initialValues.descriptionHtml ?? '',
      category: initialValues.category ?? '',
      metadata: initialValues.metadata ?? current.metadata,
    }));
  }, [
    isEditing,
    initialValues?.productName,
    initialValues?.productPage,
    initialValues?.articleNumber,
    initialValues?.isPublic,
    initialValues?.descriptionHtml,
    initialValues?.category,
    initialValues?.metadata,
  ]);

  // Link-share mode only when the server value changes (load / successful save).
  useEffect(() => {
    if (!initialValues || !isEditing) return;
    const isPublic = initialValues.isPublic ?? false;
    const mode = (initialValues.linkShareMode ?? 'open') as LinkShareMode;
    form.setFieldValue('isPublic', isPublic);
    form.setFieldValue('linkShareMode', isPublic ? 'open' : mode);
    form.setFieldValue('linkSharePassword', '');
    form.setFieldValue('updateLinkSharePassword', false);
  }, [isEditing, initialValues?.linkShareMode, initialValues?.isPublic]);

  useEffect(() => {
    onValuesChange?.(form.values);
  }, [form.values, onValuesChange]);

  useEffect(() => {
    if (!modelUploadProductName) return;
    form.setFieldValue('productName', modelUploadProductName);
  }, [modelUploadProductName]);

  const [tagsError, setTagsError] = useState<string | null>(null);

  const metadataRows = form.values.metadata?.rows ?? {};
  const tagsRowKey = Object.keys(metadataRows)[0] || 'default';
  const tagsValue = metadataRows[tagsRowKey]?.data?.Tags ?? '';

  const setMetadataTags = (newTagsStr: string) => {
    setTagsError(null);
    const meta = { ...form.values.metadata! };
    if (!meta.rows) meta.rows = {};
    if (!meta.rows[tagsRowKey]) meta.rows[tagsRowKey] = { pNetId: tagsRowKey, data: {} };
    meta.rows[tagsRowKey] = {
      ...meta.rows[tagsRowKey],
      data: { ...meta.rows[tagsRowKey].data, Tags: newTagsStr },
    };
    form.setFieldValue('metadata', meta);
  };

  const handleSubmit = (values: ProductFormData) => {
    const rows = values.metadata?.rows ?? {};
    const rowKey = Object.keys(rows)[0] || 'default';
    const tags = tagsStringToArray(rows[rowKey]?.data?.Tags ?? '');
    if (tags.length === 0) {
      setTagsError(t('editProduct.tags.atLeastOneRequired'));
      return;
    }
    setTagsError(null);
    onSubmit({
      ...values,
      isPublic: values.isPublic,
      linkShareMode: values.isPublic ? 'open' : (values.linkShareMode ?? 'open'),
    });
  };

  return (
    <form
      id={formId}
      encType="multipart/form-data"
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <h2>{t('editProduct.productData')}</h2>

      <TextInput
        label={t('editProduct.productName')}
        description={t('editProduct.productNameHint')}
        placeholder={t('editProduct.productNamePlaceholder')}
        {...form.getInputProps('productName')}
        mb="md"
        required
      />

      <TextInput
        label={t('editProduct.articleNumber')}
        placeholder={t('editProduct.articleNumberPlaceholder')}
        {...form.getInputProps('articleNumber')}
        mb="md"
      />

      <TextInput
        label={t('editProduct.productPage')}
        placeholder={t('editProduct.productPagePlaceholder')}
        {...form.getInputProps('productPage')}
        mb="md"
      />

      <Switch
        label={t('editProduct.publiclyVisible')}
        checked={form.values.isPublic}
        onChange={(event) => {
          const isPublic = event.currentTarget.checked;
          form.setFieldValue('isPublic', isPublic);
          if (isPublic) {
            form.setFieldValue('linkShareMode', 'open');
            form.setFieldValue('linkSharePassword', '');
            form.setFieldValue('updateLinkSharePassword', false);
          }
        }}
        mb="md"
      />

      <Box mb="md">
        <LinkAccessControl
          accentColor={color}
          linkShareMode={form.values.linkShareMode}
          onLinkShareModeChange={(mode) => {
            form.setFieldValue('linkShareMode', mode);
            if (mode === 'open' || mode === 'restricted') {
              form.setFieldValue('linkSharePassword', '');
              form.setFieldValue('updateLinkSharePassword', false);
            }
          }}
          isPublic={form.values.isPublic}
          showSectionTitle
          sectionHelp={
            form.values.isPublic
              ? t('editProduct.linkShare.sectionHelpPublic')
              : t('editProduct.linkShare.sectionHelp')
          }
          passwordAlreadySet={isEditing && initialLinkSharePasswordSet}
          updatePassword={form.values.updateLinkSharePassword}
          onUpdatePasswordChange={(checked) => {
            form.setFieldValue('updateLinkSharePassword', checked);
            if (!checked) form.setFieldValue('linkSharePassword', '');
          }}
          password={form.values.linkSharePassword}
          onPasswordChange={(value) => form.setFieldValue('linkSharePassword', value)}
        />
      </Box>

      {/* Temporarily hidden — categories UI disabled
      <Box mb="md">
        <Select
          label={t('editProduct.category')}
          placeholder={t('editProduct.categoryPlaceholder')}
          clearable
          searchable
          data={PRODUCT_CATEGORIES.map((slug) => ({
            value: slug,
            label: t(`productCategories.${slug}`),
          }))}
          value={form.values.category || null}
          onChange={(value) => form.setFieldValue('category', value ?? '')}
          nothingFoundMessage={t('editProduct.categoryNone')}
          mb="xs"
        />
        <Text size="xs" c="dimmed">
          {t('editProduct.categoryHint')}
        </Text>
      </Box>
      */}

      <Box mb="md">
        <TagsField
          suggestedTags={suggestedTags}
          previousTags={previousTags}
          value={tagsValue}
          onChange={setMetadataTags}
          label={t('editProduct.tags.label')}
          required
          error={tagsError}
        />
      </Box>

      <ProductRichTextEditor
        label={t('editProduct.descriptionLabel')}
        value={form.values.descriptionHtml}
        onChange={(html) => form.setFieldValue('descriptionHtml', html)}
        disabled={isDisabled}
      />

      <Text 
        size="sm" 
        c="dimmed" 
        mb="md" 
        style={{ 
          fontStyle: 'italic',
          lineHeight: 1.4,
          padding: '12px',
          backgroundColor: 'var(--mantine-color-default)',
          borderRadius: '6px',
          border: '1px solid var(--mantine-color-default-border)'
        }}
      >
        {t('editProduct.complianceNotice')}
      </Text>

      <Accordion defaultValue={null} variant="separated" mb="md">
        <Accordion.Item value="metadata">
          <Accordion.Control>
            <Text fw={600}>{t('editProduct.specifications')}</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <MetadataTableEditor
              value={form.values.metadata}
              onChange={(newMetadata) => form.setFieldValue('metadata', newMetadata)}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {!hideSubmitButton && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '10px' }}>
          <Button
            type="submit"
            disabled={isDisabled || isSubmitting}
            loading={isSubmitting}
            color={color}
          >
            {isEditing ? t('editProduct.update') : t('editProduct.createProduct')}
          </Button>
        </div>
      )}
    </form>
  );
}
